'use client';

import { Plus } from 'lucide-react';

import { Button } from '@naumu/ui/components/button';
import { cn } from '@naumu/ui/utils/cn';

interface ComposerDropHintProps {
	/**
	 * Expanded = the composer is empty: the "+" reads as the prominent primary CTA
	 * and the "or just add your files" label is shown beside it. Collapsed = the
	 * user has typed or staged a file: the label pushes into the button and fades,
	 * and the "+" eases down to the regular (non-prominent) attachments button.
	 */
	expanded: boolean;
	/** True while files are being dragged over the composer (local useFileDrop signal). */
	dragging?: boolean;
	disabled?: boolean;
	/** Opens the file picker — this affordance stands in for the "+" attachments button. */
	onBrowse?: () => void;
}

/**
 * The "or just add your files" affordance that doubles as a landing composer's
 * bottom-left "+" attachments button (rendered via LandingComposer's `dropHint`
 * slot, shared by the hero and the bottom CTA). It stays mounted across states
 * so the empty→engaged change animates: the label collapses into the button
 * while fading, and the button transitions from the prominent `solid-strong`
 * variant to the regular `ghost-muted` one.
 *
 * Clicking the "+" opens the file picker (it IS the add-files control here);
 * dragging files over the composer flips the label to its drop-active copy.
 *
 * The resting label says "add", never "drop" (W7 F8 - the site now has one CTA
 * frame and "drop" reads as discard to a cold reader). The mid-drag label keeps
 * "Drop to build your map": at that point the pointer is literally holding a
 * file over the box, so "drop" is the physical instruction, not a verb choice.
 */
export const ComposerDropHint = ({
	expanded,
	dragging = false,
	disabled,
	onBrowse,
}: ComposerDropHintProps) => {
	return (
		<div className="flex items-center">
			<Button
				type="button"
				variant={expanded ? 'solid-strong' : 'ghost-muted'}
				size="sm"
				iconOnly
				disabled={disabled}
				aria-label="Add files"
				onClick={onBrowse}
				className="transition-[color,background-color,box-shadow] duration-200"
			>
				<Plus strokeWidth={2} />
			</Button>
			<span
				aria-hidden={!expanded}
				className={cn(
					'overflow-hidden whitespace-nowrap text-[15px] font-semibold text-neutral transition-all duration-200 ease-out',
					expanded
						? 'ml-2 max-w-[260px] translate-x-0 opacity-100'
						: 'ml-0 max-w-0 -translate-x-2 opacity-0',
				)}
			>
				{dragging ? 'Drop to build your map' : 'or just add your files'}
			</span>
		</div>
	);
};
